import type { NextPage } from "next";
import { Hero } from "../../components/Hero/Hero";
import { Card } from "../../components/Card/Card";
import { EmailBanner } from "../../components/EmailBanner/EmailBanner";
import { Link, IconWithLabel } from "../../components/Actions";
import { Video } from "../../components/Video/Video";
import { MediaAndText } from "../../components/MediaAndText/MediaAndText";
import { Carousel } from "../../components/Carousel/Carousel";
import { FadeText } from "../../components/Animation/FadeText";
import { GiantPill } from "../../components/GiantPill/GiantPill";
import { PreviewWrapper } from "../../components/PreviewWrapper/PreviewWrapper";

import TwitterLogo from "../../assets/logos/twitterWhite.svg";
import LinkedinLogo from "../../assets/logos/linkedin.svg";

import Tick from "../../assets/Tick.svg";

import NextImage from "next/legacy/image";

import zackOutline from "../../assets/home/zackOutline.png";
import notePad from "../../assets/home/notePad.png";

import carousel4 from "../../assets/home/carousel4.png";
import carousel2 from "../../assets/home/carousel2.png";
import carousel3 from "../../assets/home/carousel3.png";

import emailSignup from "../../assets/home/emailSignup.png";

import fromLeft from "../../assets/about/fromLeft.png";
import fromRight from "../../assets/about/fromRight.png";
import fromTrustless from "../../assets/fromTrustless.png";

import SEO from "../../components/Seo/Seo";
import NextLink from "next/link";
import { FeaturedPress } from "../../components/FeaturedPress";
import { links } from "../../utils/links";

const slides = [
  <MediaAndText
    key="1"
    img={carousel3}
    title="You tell us."
    text="We may be editorially independent, but we’re dependent on you. You tell us what to cover and which guests to talk to, and we’ll come back with the full story and the best takes."
  />,
  <MediaAndText
    key="2"
    img={carousel2}
    title="Don’t call it a Kickstarter."
    text="Because you’re not making a donation. When you buy a membership pass, that money goes directly into the Coinage treasury, and you can vote on how to use it."
  />,

  <MediaAndText
    key="3"
    img={carousel4}
    lastChild
    title="Here’s the breakdown."
    text="Coinage is a co-operative, which is split evenly between our Network and Caucus members. Subscribers can vote, but they can’t join the co-op or share in net proceeds."
  />
];

const About: NextPage<{ preview: boolean }> = ({ preview }) => {
  return (
    <PreviewWrapper preview={preview}>
      <SEO />
      <main className="home-main overflow-hidden">
        <Hero />
        <FeaturedPress />

        <div className="bg-coinage-orange text-coinage-offWhite">
          <div className="content-wrapper">
            <div className="inner-wrapper flex-col">
              <div className="inner-child tab:w-1/2">
                <FadeText
                  textStyle="display2"
                  className="m-4 mb-0 pt-[64px] tab:pt-[160px] text-left mt-0 tab:text-left tab:mb-[100px]">
                  LEVEL THE PLAYING FIELD
                </FadeText>
              </div>
              <div className="inner-child tab:w-1/2">
                <p className=" m-4 text-left tab:text-left tab:pt-[160px] body1 text-coinage-offWhite">
                  Do you know what’s going on? Seriously, we’re asking. Things
                  move fast, and it’s hard to tell the brilliance from the
                  bullshit.
                </p>
                <p className=" m-4 text-left tab:text-left body1 text-coinage-offWhite">
                  That’s why our mission is to level that playing field. We’re
                  connecting the dots and telling the stories that are driving
                  the headlines, so this space is fairer and safer for everyone.
                  But we can’t do it alone.
                </p>

                <div className="m-4 mt-10 mb-10 tab:mb-20 tab:mt-10">
                  <Link
                    analyticsProperty="about"
                    modifier="primary"
                    href="/about#mint">
                    JOIN US
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="video-wrapper">
          <div className="flex content-wrapper m-4">
            <Video videoId={"Erf8ooypiQNMWkTa01L02VlwmQYeJxFOndWloUj8mnbVc"} />
          </div>
        </div>

        <div className="bg-coinage-offWhite ">
          <div className="content-wrapper pb-20">
            <FadeText
              textStyle="display1"
              className="p-4 pt-[64px] tab:pt-20 text-left tab:text-center mb-0 tab:mb-[50px]">
              MAKE IT. OWN IT.
              <br className="hidden tab:block" /> HERE’S THE DEAL
            </FadeText>
            <Carousel slides={slides} />
          </div>
        </div>

        <div className="bg-coinage-orange text-coinage-offWhite">
          <div className="content-wrapper">
            <div className="inner-wrapper flex-col-reverse pb-[100px]">
              <div className="inner-child  pt-[30px] tab:pt-10  tab:w-1/2">
                <div className="w-[239px] h-[231px] tab:w-[366px] tab:h-[355px] m-auto mt-5 rounded-left mb-10  tab:mt-20">
                  <NextImage
                    src={zackOutline}
                    layout="responsive"
                    alt="Zack Guzman"
                  />
                </div>

                <h2 className="text-center mt-0 mb-1 subhead1 ">Zack Guzman</h2>
                <p className=" text-center mt-0 mb-1 mono1 ">
                  Host &amp; Financial Reporter
                </p>

                <div className="tab:hidden mb-0 m-4 tab:mb-10">
                  <Link
                    analyticsProperty="about"
                    modifier="primary"
                    href="/about#about-zack">
                    About Zack
                  </Link>
                </div>

                <div
                  className={`flex m-4 gap-4 mb-10 items-center  tab:hidden `}>
                  <a
                    className="flex ml-auto mr-0"
                    data-splitbee-event="Click Link: Twitter"
                    href="https://twitter.com/zGuz"
                    target="_blank"
                    rel="noreferrer">
                    <TwitterLogo height={34} />
                  </a>
                  <a
                    className="flex mr-auto ml-0"
                    data-splitbee-event="Click Link: Linkedin"
                    href="https://www.linkedin.com/in/zack-guzman/"
                    target={"_blank"}
                    rel="noreferrer">
                    <LinkedinLogo height={34} />
                  </a>
                </div>
              </div>
              <div className="inner-child tab:w-1/2">
                <FadeText
                  textStyle="display2"
                  className="m-4 pt-20 text-left mt-0 display2 mb-0 tab:text-lef tab:mb-1 lrg:pt-[120px]">
                  LOOK WHO’S PUTTING ON A SHOW.
                </FadeText>

                <p className=" body1 m-4 text-left mb-1 pb-4 mt-0 pt-[24px] tab:text-left tab:pt-3">
                  <span className="bold">Meet your host, Zack Guzman.</span>{" "}
                  He’s a former Yahoo Finance anchor and CNBC reporter who
                  thinks the only way to cover crypto is with the help of a
                  community. Don’t like that? Vote him off. We mean it.
                </p>
                <div className="hidden tab:flex mb-0 m-4 tab:mb-10">
                  <Link
                    analyticsProperty="about"
                    modifier="primary"
                    href="/about#mint">
                    REVOLT
                  </Link>
                </div>
                <div
                  className={`flex m-4 gap-4 mb-10 items-center hidden tab:flex `}>
                  <a
                    className="flex"
                    data-splitbee-event="Click Link: Twitter"
                    href="https://twitter.com/zGuz"
                    target="_blank"
                    rel="noreferrer">
                    <TwitterLogo height={34} />
                  </a>
                  <a
                    className="flex"
                    data-splitbee-event="Click Link: Linkedin"
                    href="https://www.linkedin.com/in/zack-guzman/"
                    target={"_blank"}
                    rel="noreferrer">
                    <LinkedinLogo height={34} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-coinage-offWhite ">
          <div className="content-wrapper" id="mint">
            <FadeText
              textStyle="display1"
              className=" m-4 pt-[64px] tab:pt-[100px] text-center mt-0 max-w-[780px] mx-auto">
              GET YOUR MEMBERSHIP PASS
            </FadeText>
            <p className=" m-4 mb-10 body1 text-center text-coinage-gray tab:mb-10">
              Finally, a membership that pays dividends. Your membership pass is
              your ticket into owning the narrative. Subscribers can vote on
              Coinage content, but only Caucus and Network members can join the
              DAO/co-op. Still not sold?{" "}
              <NextLink href="/trial/mint" className="font-bold underline">
                Buy a 30-day trial pass
              </NextLink>{" "}
              to try us out.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3  pb-[60px] tab:pb-[120px] ml-auto mr-auto">
              <Card
                showCoopOwnerShip={false}
                title="Subscriber"
                active={true}
                amount={"∞"}
                tokenId={3}
                cost={0.02}
                currency="ETH"
                subscriberPass
                href="/subscriber/mint"
                linkText="Mint"
                features={[
                  "Watch ad free",
                  "Discuss editorial content",
                  "Get early access to content and experiences"
                ]}
                listTitle="Control the narrative:"
              />
              <Card
                listTitle="Own the narrative:"
                holderLink={{
                  label: "Activate Membership",
                  href: "/coop/onboarding"
                }}
                showCoopOwnerShip
                title="Caucus"
                active={true}
                amount={5000}
                tokenId={2}
                cost={0.11}
                currency="ETH"
                href="/caucus/mint"
                discountText={
                  "Existing holders, register here for a path to dividends"
                }
                pillLink={"/coop/onboarding"}
                linkText="Mint"
                features={[
                  "All Subscriber benefits, plus...",
                  "Get the option to join the co-op",
                  "Collaborate on scripts",
                  "Weigh in on Coinage experiments"
                ]}
              />
              <Card
                holderLink={{
                  label: "Activate Membership",
                  href: "/coop/onboarding"
                }}
                listTitle="Direct the project:"
                showCoopOwnerShip
                title="Network"
                active={true}
                amount={1000}
                tokenId={1}
                cost={1.1}
                currency={"ETH"}
                href="/network/mint"
                linkText="MINT"
                features={[
                  "All Caucus benefits, plus...",
                  "Enjoy a larger ownership stake",
                  "Develop roadmaps for the future",
                  "Get a direct line to our host"
                ]}
                discountText={
                  "Existing holders, register here for a path to dividends"
                }
                pillLink={"/coop/onboarding"}
              />
            </div>
          </div>
        </div>

        <div className="overflow-hidden  relative">
          <div className="w-full h-full absolute banner-image-holder z-0">
            <NextImage layout="responsive" alt={"title"} src={emailSignup} />
          </div>
          <div
            className="content-wrapper relative z-1 pt-[36px] tab:pt-[100px] pb-0 tab:pb-10"
            id="email-signup">
            <EmailBanner text="Never miss an update, including membership pass drops and show announcements." />
          </div>
        </div>

        <div className="bg-coinage-offWhite pb-[64px] tab:pb-[120px]">
          <div className="content-wrapper ">
            <h2 className="display2 tab:display1 pt-[64px] p-5 text-center mt-0 pb-2 tab:p-20 tab:pt-[120px] tab:pb-[30px] tab:max-w-[1200px] mx-auto tab:px-0">
              WHERE WE’RE GOING WITH all THIS.
            </h2>

            <p className="body1 text-center mb-10 mx-5">
              We’ve been hard at work bringing Coinage to life already. Take a
              look at where we’ve been and where we’re headed.
            </p>

            <GiantPill hideAssetOnMobile image={notePad}>
              <div className="flex flex-col  mt-5 mb-5  ">
                <p className="mono2 text-coinage-orange mb-1">Up Next</p>
                <h3 className="subhead1 text-coinage-black mb-2">
                  Season 2 Development
                </h3>
                <div className="flex flex-col  gap-4 pill-container">
                  <p className="body1 flex">
                    <Tick width="15" stroke="#388697" className="mr-2 mt-2" />
                    Co-create Season 2 roadmap
                  </p>
                  <p className="body1 flex">
                    <Tick width="15" stroke="#388697" className="mr-2 mt-2" />
                    Institutionalize Coinage Co-Op
                  </p>
                </div>
                <div>
                  <div className="button-holder-roadmap mt-8">
                    <Link
                      analyticsProperty="roadmap"
                      modifier="primary"
                      href="/roadmap">
                      SEE ROADMAP
                    </Link>
                  </div>
                </div>
              </div>
            </GiantPill>
          </div>
        </div>

        <div className="bg-coinage-purple">
          <div className="content-wrapper ">
            <h2 className="display1 pt-[64px]  p-4 tab:pt-20 text-center mt-0 text-coinage-offWhite">
              Have a Story?
            </h2>

            <p className="body1 text-center p-4 text-coinage-offWhite">
              Join us on Discord to share it, and be a part of the public
              community we’re building.
            </p>
            <p className="mono1 text-center text-coinage-blue pb-20 mt-4">
              <Link
                modifier="tertiary"
                analyticsProperty="discord"
                href={links.discord}>
                <IconWithLabel icon="external" label="DISCORD" />
              </Link>
            </p>
          </div>
        </div>

        <div className="bg-coinage-orange text-coinage-offWhite pb-[64px]">
          <div className="content-wrapper">
            <FadeText
              textStyle="display1"
              className="p-4 pt-[64px] tab:pt-[120px] display1 text-center">
              FROM
              <br />
              TRUSTLESS MEDIA.
            </FadeText>

            <p className="body1 flex px-5 ml-[auto] mr-[auto] mb-5 tab:mb-10 text-center text-coinage-offWhite max-w-[600px]">
              Coinage is the first project from Trustless Media — a production
              hub that aims to be more fair, more responsive, and way more
              interesting.
            </p>

            <p className="mono1 text-center text-coinage-blue pb-10 tab:pb-20 mt-4 max-w-[80%] mx-auto tab:max-w-[300px] w-full">
              <Link
                modifier="primary"
                analyticsProperty="discord"
                href="https://www.trustless.media">
                <IconWithLabel icon="external" label="Visit Us" />
              </Link>
            </p>

            <div className="about-photo-grid grid grid-cols-3 gap-[12px] tab:gap-4 mb-[40px] tab:mb-[160px] ">
              <div className="rounded-[12px] tab:rounded-3xl  border-coinage-offWhite translate-y-[60px] ">
                <NextImage layout="responsive" alt={"title"} src={fromLeft} />
              </div>
              <div className="rounded-[12px] tab:rounded-3xl  border-coinage-offWhite transform  ">
                <NextImage
                  layout="responsive"
                  alt={"title"}
                  src={fromTrustless}
                />
              </div>
              <div className=" rounded-[12px] tab:rounded-3xl  border-coinage-offWhite  translate-y-[60px]">
                <NextImage layout="responsive" alt={"title"} src={fromRight} />
              </div>
            </div>
          </div>
        </div>
      </main>
    </PreviewWrapper>
  );
};

export default About;
